import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  getTenant,
  isSharedHost,
  getStoredSite,
  setStoredSite,
  clearStoredSite,
  markSiteHandoff,
  getSiteHandoff,
  clearSiteHandoff,
} from '@config/tenant'
import { parseSiteHint, siteOfAccessToken } from '@config/siteHint'
import LoadingSpinner from '@components/common/LoadingSpinner'
import { HOME_ROUTE } from '@config/featureFlags'

/**
 * Resolve the Auth0 organization for a site (the getorgid handshake).
 *
 * Throws with a `cause` of 'unreachable' or 'refused' so the caller can tell the
 * two apart STRUCTURALLY. Do not go back to sniffing `err.message`: CB returns
 * several different sentences through the refused branch — "Organization not
 * found" for a site that does not exist, but also "Bad handshake; cannot verify
 * user" when the build is missing VITE_AUTHORIZE_CB_SECRET (see CLAUDE.md →
 * Deploy). Those need different words in front of the user, and matching on
 * message text is exactly how this repo has mis-reported state before.
 */
const getOrganizationId = async (siteHostname) => {
  let response
  try {
    response = await fetch('/api/auth/mobile/api.svc/json/userauth/getorgid', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site: siteHostname, secret: import.meta.env.VITE_AUTHORIZE_CB_SECRET }),
    })
  } catch (err) {
    throw Object.assign(new Error(err?.message || 'Network request failed'), { cause: 'unreachable' })
  }

  if (!response.ok) {
    throw Object.assign(new Error(`HTTP ${response.status}`), { cause: 'unreachable' })
  }

  const data = await response.json().catch(() => null)

  if (!data || data.error_code !== 200 || !data.orgid) {
    throw Object.assign(new Error(data?.error_message || 'Organization not found'), { cause: 'refused' })
  }

  return data.orgid
}

const Login = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState(null)
  const sharedHost = isSharedHost()

  // `/login?changesite=1` — the escape hatch that works even mid-redirect-loop
  // (CC-53416). Everything else on this screen is a button the user can only
  // reach if something rendered; this one is a URL, so it can be typed, sent by
  // support, or bookmarked. Read once at mount: it seeds state, it is not a mode.
  const wantsChangeSite = searchParams.get('changesite') !== null

  // `/login?site=<tenant>` — how Core's "Report Center" link says which firm the
  // user came from (spec 210, CC-54621), so they skip the site prompt. Shared host
  // only (a subdomain host's tenant is its hostname), and `changesite` wins: that
  // one is the user asking for a different site. '' = no hint, null = a hint we
  // cannot use. Read once at mount, like `changesite`.
  const [siteHint] = useState(() =>
    sharedHost && !wantsChangeSite ? parseSiteHint(searchParams.get('site')) : ''
  )
  // A link that named no usable site gets the prompt, never a silent fall-back to
  // whichever firm this browser remembers.
  const [badHint, setBadHint] = useState(() => siteHint === null)
  // Set when a signed-in session is not the hinted firm's (or cannot be read).
  const [siteConflict, setSiteConflict] = useState(null)

  // On the shared host the site name isn't in the hostname — the user picks it
  // once (or we reuse the remembered one). Elsewhere the tenant is the subdomain.
  const [site, setSite] = useState(() => {
    if (sharedHost && wantsChangeSite) {
      clearStoredSite()
      clearSiteHandoff()
      return ''
    }
    // A usable hint is remembered exactly as a typed site would be. Not while
    // signed in: whether that session is the hinted firm's is checked below, and
    // a link must never quietly re-point a signed-in session at another firm.
    if (siteHint && !isAuthenticated) setStoredSite(siteHint)
    return sharedHost ? getStoredSite() : getTenant()
  })

  // True when the remembered site already went to Auth0 in this tab and came back
  // unauthenticated. Redirecting again would rebuild the exact dead end, so stop
  // and show the prompt. Seeded once, at mount, from the marker's value at that
  // instant — `doLogin` sets the marker on the way out, and re-reading it later
  // would make the component intercept its own in-flight attempt.
  const [stalled, setStalled] = useState(
    () => sharedHost && !wantsChangeSite && !!getStoredSite() && getSiteHandoff() === getStoredSite()
  )

  // Pre-fill so a one-character typo is a one-character fix, not a retype. After an
  // unusable hint, offer the remembered site to confirm rather than assume it.
  const [siteInput, setSiteInput] = useState(() => (stalled || badHint ? getStoredSite() : ''))

  const needsSite = sharedHost && (!site || stalled || (badHint && !isAuthenticated))

  useEffect(() => {
    if (isAuthenticated) {
      // The round trip worked — retire the marker so a later, legitimate re-login
      // isn't mistaken for the failed one.
      clearSiteHandoff()
      if (!siteHint) {
        navigate(HOME_ROUTE, { replace: true })
        return
      }
      // Already signed in, and the link names a firm: go on only if this session
      // IS that firm's, per its token's `cbsite` claim (what cb-api routes by).
      // Anything else — another firm, or a token we cannot read — is the user's
      // call, never a guess: this app shows one firm's financial data.
      let cancelled = false
      getAccessTokenSilently()
        .then(siteOfAccessToken, () => null)
        .then((signedIn) => {
          if (cancelled) return
          if (signedIn === siteHint) {
            setStoredSite(siteHint)
            navigate(HOME_ROUTE, { replace: true })
          } else {
            setSiteConflict({ signedIn })
          }
        })
      return () => { cancelled = true }
    }
    if (needsSite) return // wait for the site-name prompt

    const doLogin = async () => {
      try {
        const organizationId = await getOrganizationId(`${site}.centerbase.com`)
        // Stamp BEFORE the redirect: once loginWithRedirect fires we are on
        // Auth0's domain and no cb2 code runs again until the user comes back.
        markSiteHandoff(site)
        await loginWithRedirect({
          authorizationParams: {
            redirect_uri: `${window.location.origin}/ssocallback`,
            organization: organizationId,
          },
        })
      } catch (err) {
        setError({ message: err.message, cause: err.cause })
      }
    }

    doLogin()
  }, [isAuthenticated, loginWithRedirect, getAccessTokenSilently, navigate, needsSite, site, siteHint])

  const submitSite = (e) => {
    e.preventDefault()
    const s = siteInput.trim().toLowerCase()
    if (!s) return
    // An explicit submit always gets a real attempt, including a retry of the same
    // site — the loop-breaker exists to stop SILENT re-redirects, never to stop the
    // user from trying again on purpose.
    clearSiteHandoff()
    setStalled(false)
    setBadHint(false)
    setError(null)
    setStoredSite(s)
    setSite(s) // → effect runs doLogin
  }

  const tryDifferentSite = () => {
    clearStoredSite()
    clearSiteHandoff()
    setStalled(false)
    setBadHint(false)
    setSite('')
    setSiteInput('')
    setError(null)
  }

  // Leave the signed-in session for the hinted firm. Header's "Switch site" way:
  // clear the SDK's cache and do a full load, never Auth0 logout() (it needs every
  // host in Allowed Logout URLs). With refresh tokens and no iframe fallback the
  // reload cannot quietly sign back in to the old firm, so /login then signs in to
  // the remembered — now hinted — site through the normal path.
  const signInToHintedSite = () => {
    setStoredSite(siteHint)
    clearSiteHandoff()
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('@@auth0spajs@@')) localStorage.removeItem(key)
    }
    window.location.replace('/login')
  }

  if (siteConflict) {
    const { signedIn } = siteConflict
    return (
      <div className="min-h-screen bg-cb-navy flex items-center justify-center">
        <div role="alert" className="bg-white rounded-2xl shadow-2xl p-10 max-w-md w-full mx-4 text-center">
          <h1 className="text-2xl font-display text-cb-navy mb-4">Centerbase</h1>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {signedIn ? 'You’re signed in to a different site' : 'We couldn’t confirm your site'}
          </h2>
          <p className="text-gray-600 mb-6">
            {signedIn ? (
              <>Report Center is signed in to <span className="font-medium">{signedIn}</span>, but the link you followed is for <span className="font-medium">{siteHint}</span>.</>
            ) : (
              <>Report Center is already signed in, but we couldn’t tell which site. The link you followed is for <span className="font-medium">{siteHint}</span>.</>
            )}
          </p>
          <button
            onClick={signInToHintedSite}
            className="w-full py-3 px-6 bg-cb-accent-text text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Sign in to {siteHint}
          </button>
          <button
            onClick={() => navigate(HOME_ROUTE, { replace: true })}
            className="mt-3 w-full py-3 px-6 bg-white text-cb-accent-text border border-rc-control rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            {signedIn ? `Continue to ${signedIn}` : 'Continue anyway'}
          </button>
        </div>
      </div>
    )
  }

  // Signed in with a hint: checking whose session this is (a token read).
  if (isAuthenticated && siteHint) {
    return (
      <div className="min-h-screen bg-cb-navy flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  // Site-name prompt (shared host: no remembered site, an explicit ?changesite,
  // an unusable ?site, or a handoff that came back unauthenticated).
  if (needsSite) {
    return (
      <div className="min-h-screen bg-cb-navy flex items-center justify-center">
        <form onSubmit={submitSite} className="bg-white rounded-2xl shadow-2xl p-10 max-w-md w-full mx-4">
          <h1 className="text-2xl font-display text-cb-navy mb-1 text-center">Centerbase</h1>
          {stalled ? (
            // role="alert" so this is ANNOUNCED, not merely visible — the user
            // arrives here having been bounced, and needs to know why.
            <p role="alert" id="cb-site-help" className="text-gray-600 text-sm mb-6 text-center">
              We couldn’t sign you in to <span className="font-medium">{getStoredSite()}</span>.
              Check the site name, or enter a different one.
            </p>
          ) : badHint ? (
            // Announced for the same reason: a link sent the user here expecting
            // to skip this screen.
            <p role="alert" id="cb-site-help" className="text-gray-600 text-sm mb-6 text-center">
              The link you followed didn’t include a usable site name. Enter your site name to continue.
            </p>
          ) : (
            <p id="cb-site-help" className="text-gray-600 text-sm mb-6 text-center">
              Enter your site name to continue.
            </p>
          )}
          <label htmlFor="cb-site" className="block text-sm font-medium text-gray-700 mb-1">Site name</label>
          <div className="flex items-stretch rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-cb-accent/30 focus-within:border-cb-accent overflow-hidden">
            <input
              id="cb-site"
              type="text"
              autoFocus
              value={siteInput}
              onChange={(e) => setSiteInput(e.target.value)}
              placeholder="yoursite"
              aria-label="Site name"
              aria-describedby="cb-site-help"
              className="flex-1 px-3 py-2.5 text-sm text-gray-900 focus:outline-none"
            />
            <span className="px-3 py-2.5 text-sm text-gray-500 bg-gray-50 border-l border-gray-200 select-none">.centerbase.com</span>
          </div>
          <button
            type="submit"
            className="mt-6 w-full py-3 px-6 bg-cb-accent-text text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Continue
          </button>
        </form>
      </div>
    )
  }

  if (error) {
    // 'unreachable' = we never got an answer (network/HTTP). 'refused' = the
    // handshake completed and CB declined. A WRONG PASSWORD never reaches this
    // screen at all — credentials are entered on Auth0's page and a failure there
    // returns to /ssocallback, which renders its own "Authentication Error". That
    // is the tenant-vs-credentials distinction CC-53416 asks for: the two failures
    // already live on two different screens, and this one is only ever about the
    // site.
    const unreachable = error.cause === 'unreachable'
    return (
      <div className="min-h-screen bg-cb-navy flex items-center justify-center">
        <div role="alert" className="bg-white rounded-2xl shadow-2xl p-10 max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {unreachable ? 'Can’t reach Centerbase' : `Can’t sign in to ${site}`}
          </h2>
          <p className="text-gray-600 mb-6">
            {unreachable
              ? 'We couldn’t reach Centerbase to look up this site. Check your connection and try again.'
              : error.message}
          </p>
          <p className="text-sm text-gray-500 mb-6">Site: {site}.centerbase.com</p>
          {sharedHost ? (
            <button
              onClick={tryDifferentSite}
              className="w-full py-3 px-6 bg-cb-accent-text text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Try a different site
            </button>
          ) : (
            // Subdomain hosts take the tenant from the URL, so there is no field to
            // return to — reloading is the honest affordance. To use another
            // tenant here you change the hostname.
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 px-6 bg-cb-accent-text text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-cb-navy flex items-center justify-center">
      <div className="text-center">
        <div className="mb-8">
          <svg className="w-16 h-16 mx-auto text-white" viewBox="0 0 40 40" fill="none" aria-hidden="true">
            <rect width="40" height="40" rx="8" fill="#00a3ff" />
            <path d="M12 20a8 8 0 1116 0 8 8 0 01-16 0z" stroke="white" strokeWidth="2.5" fill="none" />
            <path d="M20 12v4m0 8v4m-8-8h4m8 0h4" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <h1 className="text-2xl font-display text-white mb-3">Centerbase</h1>
        <p className="text-white text-sm font-medium mb-6">
          Connecting to {site}.centerbase.com...
        </p>
        <LoadingSpinner />
        {/* The last chance to bail before Auth0 takes the page. Reachable by
            keyboard (a real button, next in tab order after nothing else focusable
            on this screen) and worth having even though the redirect is usually
            fast: when getorgid is slow, this is the only rendered escape, and on
            the shared host it is the difference between "wrong firm, one click" and
            "wrong firm, clear your site data". */}
        {sharedHost && (
          <button
            onClick={tryDifferentSite}
            className="mt-6 text-sm text-white underline underline-offset-4 hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white rounded"
          >
            Use a different site
          </button>
        )}
      </div>
    </div>
  )
}

export default Login
